import React from 'react';
import { Divider, Form, Grid, Transition } from 'semantic-ui-react';
import { Dispatch } from 'redux';
import { connect } from 'react-redux';

import config from 'helpers/config';
import axios from 'helpers/axios';
import { displayConfirmationModal } from 'helpers/services/removeConfirmationService';
import { deleteVehicle as deleteLoaner } from '../../../actions/vehicleActions';

import ModalStateType from 'models/generic/ModalState';
import VehicleModel from 'models/VehicleModel';
import FileModel from 'models/FileModel';
import PhotoManagementModal from '../../common/PhotoManagementModal';
import Modal from '../../common/Modal';
import VINSearch from './VINSearch';
import { Link } from 'react-router-dom';

interface Props {
    loanerId?: string;
    onClose: (vehicleCreated?: boolean, vehicleUpdated?: boolean, loanerId?: string) => void;
    dispatch: Dispatch;
}

interface State {
    modalState: ModalStateType;
    modalHeader: string;
    loaner: VehicleModel;
    isLoading: boolean;
    isFormExpanded: boolean;
    isImagesModalOpen: boolean;
    formErrors: any;

    isVINsearchActive: boolean;
}

class LoanerModal extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = {
            modalState: this.props.loanerId ? ModalStateType.Edit : ModalStateType.Create,
            modalHeader: '',
            isLoading: true,
            isImagesModalOpen: false,
            isFormExpanded: false,
            loaner: {} as VehicleModel,
            formErrors: {},
            isVINsearchActive: false,
        };

        this.handleChange = this.handleChange.bind(this);
        this.save = this.save.bind(this);
        this.delete = this.delete.bind(this);
        this.toggleImagesModal = this.toggleImagesModal.bind(this);
    }

    componentDidMount() {
        if (this.state.modalState === ModalStateType.Edit) {
            this.setState({ isLoading: true });
            this.getLoaner();
        } else {
            this.setState({ isLoading: false, isVINsearchActive: true });
            this.setModalHeader();
        }
    }

    getLoaner() {
        axios
            .get(`${config.apiUrl}/vehicle/${this.props.loanerId}`)
            .then((response) => {
                this.setState({ loaner: response.data, isLoading: false });
                this.setModalHeader();
            })
            .catch((_) => {
                this.setState({ isLoading: false });
            });
    }

    setModalHeader() {
        if (this.state.modalState === ModalStateType.Edit) {
            const { make, model } = this.state.loaner!;
            const modalHeader = `${make} ${model}`;
            this.setState({ modalHeader });
        } else {
            const modalHeader = 'Register new loaner vehicle';
            this.setState({ modalHeader });
        }
    }

    save() {
        if (!this.validateForm()) {
            return;
        }
        this.setState({ isLoading: true });
        const { loaner } = this.state;
        if (this.state.modalState === ModalStateType.Create) {
            this.createLoaner(loaner);
        } else {
            this.updateLoaner(loaner);
        }
    }

    validateForm() {
        const { loaner } = this.state;
        const errors: any = {};
        if (!loaner?.make || loaner?.make?.length < 1) {
            errors.make = 'Make cannot be empty';
        }

        if (!loaner?.model || loaner?.model?.length < 1) {
            errors.model = 'Model cannot be empty';
        }

        if (!loaner.year) {
            errors.year = 'Year specified';
        }

        this.setState({ formErrors: errors });
        const isFormValid = Object.keys(errors).length === 0;
        return isFormValid;
    }

    delete() {
        const { model, make, id } = this.state.loaner;
        const { dispatch, onClose } = this.props;
        displayConfirmationModal(`${model} ${make}`)
            .then(() => {
                dispatch<any>(deleteLoaner(String(id)));
                onClose(false, true);
            })
            .catch();
    }

    updateLoaner(loanerData: any) {
        axios
            .put(`${config.apiUrl}/loaner`, loanerData)
            .then((_) => this.props.onClose(false, true))
            .catch((_) => {
                this.setState({ isLoading: false });
            });
    }

    createLoaner(loanerData: any) {
        axios
            .post(`${config.apiUrl}/loaner`, loanerData)
            .then((res) => {
                this.props.onClose(true, false, res.data.loanerId);
            })
            .catch((_) => {
                this.setState({ isLoading: false });
            });
    }

    getValue(name: string) {
        // @ts-ignore
        return this.state.loaner[name] || '';
    }

    handleChange(event: React.FormEvent<HTMLInputElement> | React.FormEvent<HTMLTextAreaElement>) {
        const { name, value } = event.currentTarget;

        // @ts-ignore
        const updatedLoaner: VehicleModel = { ...this.state.loaner, [name]: value };
        this.setState({ loaner: updatedLoaner });
    }

    toggleImagesModal() {
        const { isImagesModalOpen } = this.state;
        this.setState({ isImagesModalOpen: !isImagesModalOpen });
        if (isImagesModalOpen) {
            axios
                .get(`${config.apiUrl}/file/images/`, {
                    params: { relatedEntityType: 'loaner', relatedEntityId: this.state.loaner.id },
                })
                .then((res) =>
                    this.setState((prevState) => ({
                        loaner: {
                            ...prevState.loaner,
                            files: res.data,
                        },
                    })),
                );
        }
    }

    render() {
        // prettier-ignore
        const { modalHeader, isLoading, isFormExpanded,  loaner, isImagesModalOpen, modalState, isVINsearchActive, formErrors } =
            this.state;

        return (
            <Modal
                onDelete={modalState === ModalStateType.Edit ? this.delete : undefined}
                headerText={modalHeader}
                isOpen={true}
                onClose={() => this.props.onClose(false, false)}
                onSave={this.save}
                isSaveHidden={isVINsearchActive}
                isLoading={isLoading}
            >
                {isImagesModalOpen && (
                    <PhotoManagementModal
                        onClose={this.toggleImagesModal}
                        relatedEntityTypeName="loaner"
                        relatedEntityId={loaner.id}
                        existingImages={loaner.files}
                    />
                )}
                {isVINsearchActive ? (
                    <VINSearch
                        vehicleSelected={(vehicle) => this.setState({ loaner: vehicle, isVINsearchActive: false })}
                        registerManuallySelected={() => this.setState({ isVINsearchActive: false })}
                    />
                ) : (
                    <div>
                        <Grid>
                            <Grid.Row columns={1}>
                                <Grid.Column width="16">
                                    <Form>
                                        {modalState === ModalStateType.Edit && !!loaner?.customer && (
                                            <div className="border-b border-gray-300 pb-4">
                                                <div>
                                                    <span className="inline-block font-semibold">Owner: </span>
                                                    <Link
                                                        to={`/customer/${loaner.customer?.id}`}
                                                        className="font-bold pl-4 py-2 link"
                                                    >
                                                        {loaner.customer?.firstName} {loaner.customer?.lastName}
                                                    </Link>
                                                </div>
                                            </div>
                                        )}

                                        <Form.Group unstackable widths={2} className="pt-4">
                                            <Form.Input
                                                required
                                                name={'make'}
                                                label="Make"
                                                onChange={this.handleChange}
                                                value={this.getValue('make')}
                                                error={formErrors.make}
                                            />
                                            <Form.Input
                                                required
                                                name={'model'}
                                                label="Model"
                                                onChange={this.handleChange}
                                                value={this.getValue('model')}
                                                error={formErrors.model}
                                            />
                                        </Form.Group>
                                        <Form.Group widths={2}>
                                            <Form.Input
                                                name={'series'}
                                                onChange={this.handleChange}
                                                value={this.getValue('series')}
                                                label="Trim"
                                            />
                                            <Form.Input
                                                required
                                                name={'year'}
                                                onChange={this.handleChange}
                                                value={this.getValue('year')}
                                                label="Year"
                                                error={formErrors.year}
                                            />
                                        </Form.Group>
                                        <Form.Group widths={2}>
                                            <Form.Input
                                                name={'VIN'}
                                                onChange={this.handleChange}
                                                value={this.getValue('VIN')}
                                                label="VIN"
                                            />
                                            <Form.Input
                                                name={'licensePlate'}
                                                onChange={this.handleChange}
                                                value={this.getValue('licensePlate')}
                                                label="License Plate"
                                            />
                                        </Form.Group>
                                        <Form.Group unstackable widths={2}>
                                            <Form.Input
                                                name={'transmissionType'}
                                                onChange={this.handleChange}
                                                value={this.getValue('transmissionType')}
                                                label="Transmission"
                                            />
                                            <Form.Input
                                                name={'engine'}
                                                onChange={this.handleChange}
                                                value={this.getValue('engine')}
                                                label="Engine"
                                            />
                                        </Form.Group>
                                        <Form.Group unstackable widths={2}>
                                            <Form.Input
                                                name={'color'}
                                                onChange={this.handleChange}
                                                value={this.getValue('color')}
                                                label="Color"
                                            />
                                        </Form.Group>
                                        <Divider horizontal>
                                            <span
                                                className="clickable expand-link"
                                                onClick={() => this.setState({ isFormExpanded: !isFormExpanded })}
                                            >
                                                {isFormExpanded ? 'Less' : 'More'}
                                            </span>
                                        </Divider>
                                        <Transition.Group animation="fade up" duration="200">
                                            {isFormExpanded && (
                                                <>
                                                    <Form.Group>
                                                        <Form.TextArea
                                                            width="16"
                                                            placeholder="Notes"
                                                            label="Notes"
                                                            name="notes"
                                                            onChange={this.handleChange}
                                                            value={this.getValue('notes')}
                                                        />
                                                    </Form.Group>
                                                    <Form.Group className="field">
                                                        <div className="field modal__photo-preview">
                                                            <div className="modal__photo-preview__header">
                                                                <label>Images</label>
                                                                <span>
                                                                    <small>
                                                                        <span
                                                                            className="link"
                                                                            onClick={this.toggleImagesModal}
                                                                        >
                                                                            Manage images
                                                                        </span>
                                                                    </small>
                                                                </span>
                                                            </div>

                                                            <div className="image-preview">
                                                                {loaner.files?.map((image: FileModel, index) => (
                                                                    <span key={index} className="image-item">
                                                                        <img
                                                                            src={`${config.fileRootURL}/images/${image.fileName}`}
                                                                            alt={image.fileName}
                                                                            title={image.fileName}
                                                                            width="100%"
                                                                        />
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </Form.Group>
                                                </>
                                            )}
                                        </Transition.Group>
                                    </Form>
                                </Grid.Column>
                            </Grid.Row>
                        </Grid>
                    </div>
                )}
            </Modal>
        );
    }
}

export default connect()(LoanerModal);
